import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import type { Brand } from '../../types/brand'
import type { PaginatedResponse } from '../../types/common'
import { logger } from '../../utils/logger'

export interface BrandsFilters {
  page?: number
  limit?: number
  toValidate?: boolean | null
  search?: string
}

export class BrandsService {
  /**
   * Search brands with autocomplete functionality
   */
  static async searchBrands(query: string): Promise<Brand[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await BrandsService.getMockSearchResults(query)
    }
    
    if (query.length < 3) {
      return []
    }
    
    const params = new URLSearchParams()
    params.append('search', query)
    params.append('limit', '50')
    
    const url = `/v1/brands?${params.toString()}`
    logger.log('🔍 Searching brands:', url)
    
    const response = await apiClient.get<{ brands: Brand[] }>(url)
    logger.log('✅ Brand search results:', response)
    
    return response.brands || []
  }

  /**
   * Get paginated list of brands
   */
  static async getBrands(filters: BrandsFilters = {}): Promise<PaginatedResponse<Brand>> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await BrandsService.getMockBrands(filters)
    }
    
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      params.append('toValidate', filters.toValidate.toString())
    }
    if (filters.search) params.append('search', filters.search)
    
    const queryString = params.toString()
    const url = `/v1/brands${queryString ? `?${queryString}` : ''}`
    
    logger.log('📦 Fetching brands from API...', url)
    const response = await apiClient.get<{ brands: Brand[]; totalRecords: number; currentPage: number; pageSize: number; lastPage: number; firstPage: number }>(url)
    logger.log('✅ Brands received:', response)

    return {
      data: response.brands || [],
      currentPage: response.currentPage,
      lastPage: response.lastPage,
      firstPage: response.firstPage,
      pageSize: response.pageSize,
      totalRecords: response.totalRecords,
    }
  }

  /**
   * Get a single brand by ID
   */
  static async getBrand(uuid: string): Promise<Brand> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await BrandsService.getMockBrand(uuid)
    }
    
    const brand = await apiClient.get<Brand>(`/v1/brands/${uuid}`)
    return brand
  }

  /**
   * Create a new brand
   */
  static async createBrand(brand: Omit<Brand, 'uuid'>): Promise<Brand> {
    const createdBrand = await apiClient.post<Brand>('/v1/brands', brand)
    return createdBrand
  }

  /**
   * Update an existing brand
   */
  static async updateBrand(brand: Brand): Promise<Brand> {
    if (!brand.uuid) {
      throw new Error('Brand UUID is required for update')
    }
    const updatedBrand = await apiClient.put<Brand>(`/v1/brands/${brand.uuid}`, brand)
    return updatedBrand
  }

  /**
   * Delete a brand
   */
  static async deleteBrand(uuid: string): Promise<void> {
    await apiClient.delete(`/v1/brands/${uuid}`)
  }

  /**
   * Validate a brand (set toValidate to false)
   */
  static async validateBrand(uuid: string): Promise<Brand> {
    const validatedBrand = await apiClient.put<Brand>(`/v1/brands/${uuid}/validate`, {
      toValidate: false
    })
    return validatedBrand
  }

  /**
   * Merge two brands
   */
  static async mergeBrand(brandId: string, mergeWithId: string): Promise<Brand> {
    const params = new URLSearchParams()
    params.append('mergeWith', mergeWithId)
    
    const mergedBrand = await apiClient.put<Brand>(`/v1/brands/${brandId}/merge?${params.toString()}`)
    return mergedBrand
  }

  /**
   * Mock data for development/testing
   */
  private static async getMockSearchResults(query: string): Promise<Brand[]> {
    const mockBrands: Brand[] = [
      { uuid: 'brand-1', name: 'L\'Oréal Paris', toValidate: false },
      { uuid: 'brand-2', name: 'Nivea', toValidate: false },
      { uuid: 'brand-3', name: 'Garnier', toValidate: false },
      { uuid: 'brand-4', name: 'Eucerin', toValidate: false },
      { uuid: 'brand-5', name: 'La Roche-Posay', toValidate: false },
      { uuid: 'brand-6', name: 'Vichy', toValidate: false },
      { uuid: 'brand-7', name: 'Avène', toValidate: false },
      { uuid: 'brand-8', name: 'CeraVe', toValidate: false },
      { uuid: 'brand-9', name: 'Neutrogena', toValidate: false },
      { uuid: 'brand-10', name: 'Bioderma', toValidate: false },
    ]
    
    const filteredBrands = mockBrands.filter(brand =>
      brand.name.toLowerCase().includes(query.toLowerCase())
    )
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(filteredBrands), 300)
    })
  }

  private static async getMockBrands(filters: BrandsFilters): Promise<PaginatedResponse<Brand>> {
    const allBrands: Brand[] = Array.from({ length: 50 }, (_, i) => ({
      uuid: `brand-${i + 1}`,
      name: `Brand ${i + 1}`,
      toValidate: i % 4 === 0,
    }))
    
    let filteredBrands = [...allBrands]
    
    if (filters.search) {
      const search = filters.search.toLowerCase()
      filteredBrands = filteredBrands.filter(brand => 
        brand.name.toLowerCase().includes(search)
      )
    }
    
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      filteredBrands = filteredBrands.filter(brand => brand.toValidate === filters.toValidate)
    }
    
    const page = filters.page || 1
    const limit = filters.limit || 20
    const start = (page - 1) * limit
    const paginatedBrands = filteredBrands.slice(start, start + limit)
    
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: paginatedBrands,
          currentPage: page,
          firstPage: 1,
          lastPage: Math.ceil(filteredBrands.length / limit),
          pageSize: limit,
          totalRecords: filteredBrands.length,
        })
      }, 500)
    })
  }

  private static async getMockBrand(uuid: string): Promise<Brand> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          uuid,
          name: `Mock Brand ${uuid}`,
          toValidate: false,
        })
      }, 300)
    })
  }
}